/**
 * ISO-weeksleutels: de enige periodetaal van deze module.
 *
 * De conventie staat in `ARCHITECTURE-SPINE.md`, *Consistency Conventions*: overal `YYYY-Www`,
 * als padsegment én als querywaarde. Deze code rekent niet aan metingen — hij zet dagen om in
 * weeknummers, wat navigatie is en geen uitkomst (AD-19 gaat over de cijfers in het antwoord).
 */

/**
 * De vroegste week die de kiezer mag aanbieden.
 *
 * De meting begint bij de eerste run na oplevering en oudere weken worden niet aangevuld
 * (`ARCHITECTURE-SPINE.md`, non-goals). Dit is de enige plek waar die ondergrens staat; verzet
 * hem hier en de kiezer, het adres en de terugval volgen vanzelf.
 */
export const EERSTE_GEMETEN_WEEK = '2026-W34';

const WEEKSLEUTEL = /^(\d{4})-W(0[1-9]|[1-4]\d|5[0-3])$/;
const DAG = 86_400_000;

/** True zodra `waarde` de vorm `YYYY-Www` heeft, met een weeknummer tussen 01 en 53. */
export function isWeekSleutel(waarde: string | null | undefined): boolean {
  return typeof waarde === 'string' && WEEKSLEUTEL.test(waarde);
}

/** De kalenderdag in Amsterdam, zoals de dienst de week meet (de tijdzone van de dienst is Europe/Amsterdam; besluit 19-10 #7). */
const AMSTERDAMSE_DAG = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** De Amsterdamse kalenderdag van `datum` als `YYYY-MM-DD`, zoals de dienst de dag telt. */
export function amsterdamseDag(datum: Date): string {
  return AMSTERDAMSE_DAG.format(datum);
}

/** De UTC-middernacht van de maandag waarin `datum` valt, gerekend op de Amsterdamse kalenderdag. */
function maandagVan(datum: Date): Date {
  const [jaar, maand, dag] = AMSTERDAMSE_DAG.format(datum).split('-').map(Number) as [number, number, number];
  const kalenderdag = new Date(Date.UTC(jaar, maand - 1, dag));
  // getUTCDay: zondag is 0. ISO telt maandag als dag 1, dus zondag hoort bij de wéék ervoor.
  const verschuiving = (kalenderdag.getUTCDay() + 6) % 7;
  return new Date(kalenderdag.getTime() - verschuiving * DAG);
}

/**
 * De ISO-weeksleutel van de week waarin `datum` valt.
 *
 * Het jaar in de sleutel is het jaar van de dónderdag van die week, niet het jaar van de datum
 * zelf: 1 januari 2027 valt in week 53 van 2026, en zo staat het ook in het weekdocument.
 */
export function isoWeekKey(datum: Date): string {
  const maandag = maandagVan(datum);
  const donderdag = new Date(maandag.getTime() + 3 * DAG);
  // 4 januari ligt per definitie in week 1 — 1 januari niet, want die kan nog bij het vorige jaar horen.
  const vierdeJanuari = new Date(Date.UTC(donderdag.getUTCFullYear(), 0, 4));
  const weeknummer = Math.floor((donderdag.getTime() - maandagVan(vierdeJanuari).getTime()) / (7 * DAG)) + 1;
  return `${donderdag.getUTCFullYear()}-W${String(weeknummer).padStart(2, '0')}`;
}

/** De maandag waarmee `weekKey` begint, als UTC-middernacht. */
export function maandagVanWeek(weekKey: string): Date {
  const deel = WEEKSLEUTEL.exec(weekKey);
  if (!deel) throw new Error(`Geen ISO-weeksleutel: ${weekKey}`);
  const [, jaar, week] = deel;
  const vierdeJanuari = new Date(Date.UTC(Number(jaar), 0, 4)); // ligt per definitie in week 1
  const maandag = new Date(maandagVan(vierdeJanuari).getTime() + (Number(week) - 1) * 7 * DAG);
  // Niet elk jaar heeft 53 weken. Zonder deze controle geeft `2025-W53` stil de maandag van
  // `2026-W01` terug, en dan wijst een adres naar een andere week dan het scherm toont.
  if (isoWeekKey(maandag) !== weekKey) throw new Error(`Week bestaat niet: ${weekKey}`);
  return maandag;
}

/**
 * De afgesloten weken, nieuwste bovenaan, tot en met `eersteWeek`.
 *
 * Afgesloten betekent voorbij: de week waarin `nu` valt hoort er niet bij. Dat is geen
 * afronding maar de reden dat er geen "deze week" in de kiezer staat — een halve week meten
 * levert een halve uitkomst die als een hele leest (UX-DR16).
 */
export function afgeslotenWeken(nu: Date, eersteWeek: string = EERSTE_GEMETEN_WEEK): string[] {
  // Een klok die geen datum oplevert zou hier een lege lijst geven, en dat leest als "er is nog
  // geen week afgesloten" — een onwaarheid over de meting in plaats van een fout in de aanroep.
  if (Number.isNaN(nu.getTime())) throw new Error('Geen geldige datum');
  const grens = maandagVanWeek(eersteWeek).getTime();
  const weken: string[] = [];
  for (let maandag = maandagVan(nu).getTime() - 7 * DAG; maandag >= grens; maandag -= 7 * DAG) {
    weken.push(isoWeekKey(new Date(maandag)));
  }
  return weken;
}

/**
 * De laatste `aantal` afgesloten weken, oudste eerst (SPEC-meerweekenoverzicht CAP-6, criterium 41).
 *
 * In Amsterdamse tijd, zoals de dienst afsluit; de lopende week telt nooit mee. Gerekend vanaf de
 * maandag in stappen van zeven dagen, zodat week 53 meetelt.
 */
export function laatsteAfgeslotenWeken(nu: Date, aantal: number): string[] {
  if (Number.isNaN(nu.getTime())) throw new Error('Geen geldige datum');
  const laatste = maandagVan(nu).getTime() - 7 * DAG;
  return Array.from({ length: aantal }, (_, i) => isoWeekKey(new Date(laatste - (aantal - 1 - i) * 7 * DAG)));
}

/**
 * De eerste en de laatste getoonde maand van de tab Maand, elk als UTC-middernacht van de eerste dag: de
 * laatste `maanden` volledige kalendermaanden vóór de lopende, die uit de Amsterdamse kalenderdag komt
 * (besluit 29-10-2026 ronde 2 #1 en ronde 3 #3). `maandWeken` rekent met dezelfde grenzen, dus de
 * periodetekst en de rijen kunnen niet uiteenlopen. Formatteer met `timeZone: 'UTC'`.
 */
export function maandPeriode(nu: Date, maanden: number): { van: Date; tot: Date } {
  if (Number.isNaN(nu.getTime())) throw new Error('Geen geldige datum');
  const [jaar, maand] = AMSTERDAMSE_DAG.format(nu).split('-').map(Number) as [number, number];
  return { van: new Date(Date.UTC(jaar, maand - 1 - maanden, 1)), tot: new Date(Date.UTC(jaar, maand - 2, 1)) };
}

/**
 * De weken van de tab Maand: elke afgesloten ISO-week waarvan de maandag in de laatste `maanden`
 * volledige kalendermaanden vóór de lopende valt, oudste eerst (SPEC-meerweekenoverzicht-feedback CAP-7,
 * besluit 29-10-2026 #4 en ronde 2 #1).
 *
 * De lopende maand komt uit de Amsterdamse kalenderdag; de maandagen zijn UTC-middernachten, dus de
 * vergelijking met het begin en einde van de periode hangt niet af van de klok van de browser. Geen
 * ondergrens bij `EERSTE_GEMETEN_WEEK`: weken ervóór zijn gewone rijen met "geen gegevens".
 */
export function maandWeken(nu: Date, maanden: number): string[] {
  const { van, tot } = maandPeriode(nu, maanden);
  const begin = van.getTime();
  const eind = Math.min(Date.UTC(tot.getUTCFullYear(), tot.getUTCMonth() + 1, 1), maandagVan(nu).getTime());
  let maandag = maandagVan(new Date(begin)).getTime();
  if (maandag < begin) maandag += 7 * DAG;
  const weken: string[] = [];
  for (; maandag < eind; maandag += 7 * DAG) weken.push(isoWeekKey(new Date(maandag)));
  return weken;
}

/** Het weeknummer uit "2026-W38" ("38"), of de sleutel zelf als die niet die vorm heeft. */
export function weeknummer(weekKey: string): string {
  const deel = weekKey.split('-W')[1];
  return deel !== undefined && /^\d{2}$/.test(deel) ? String(Number(deel)) : weekKey;
}
